import { Component, ErrorInfo, ReactNode } from 'react';

import { theme } from 'style/ORSNNTheme';
import { Button } from 'ui-kit';

type Props = {
  children: ReactNode;
  /** Shown as the main heading when an error is caught */
  title?: string;
  /** When this value changes (e.g. after history.back()), the error state clears */
  resetKey?: string;
  /**
   * When true, spans the full `.page-container` CSS grid so the fallback is not
   * squeezed into the first column (normal routes render multiple grid-area children).
   */
  spanPageContainerGrid?: boolean;
};

type State = {
  hasError: boolean;
};

/**
 * Catches render-phase errors in the subtree so a single failure does not
 * replace the entire app with a blank document.
 */
export class ErrorBoundary extends Component<Props, State> {
  public state: State = { hasError: false };

  public static getDerivedStateFromError(_error: Error): State {
    return { hasError: true };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('[ErrorBoundary]', {
      message: String(error?.message ?? 'unknown').slice(0, 300),
      name: error?.name ?? 'Error',
      path: typeof window !== 'undefined' ? window.location.pathname : '',
      componentStack: errorInfo.componentStack?.trim().slice(0, 400) ?? '',
    });
  }

  public componentDidUpdate(prevProps: Readonly<Props>): void {
    if (
      this.state.hasError &&
      this.props.resetKey !== undefined &&
      this.props.resetKey !== prevProps.resetKey
    ) {
      this.setState({ hasError: false });
    }
  }

  private handleGoBack = (): void => {
    window.history.back();
  };

  private handleReload = (): void => {
    window.location.reload();
  };

  public render(): ReactNode {
    if (this.state.hasError) {
      const title = this.props.title ?? 'Something went wrong';
      const { spanPageContainerGrid } = this.props;
      return (
        <div
          data-testid="error-boundary-fallback"
          className={
            spanPageContainerGrid
              ? 'col-span-full row-span-full box-border flex h-full min-h-full w-full min-w-0 flex-col items-center justify-center px-6 py-12 text-center'
              : 'box-border flex min-h-screen w-full flex-col items-center justify-center px-6 py-12 text-center'
          }
          style={{
            backgroundColor: theme.color.bgApp,
            color: theme.color.fgDefault,
          }}
        >
          <div className="flex w-full max-w-md flex-col items-center gap-6">
            <h1 className="text-lg font-semibold leading-snug">{title}</h1>
            <p className="text-sm leading-relaxed" style={{ color: theme.color.fgMuted }}>
              Try going back to the previous page, or reload the page to continue. If the problem
              keeps happening, try turning off browser extensions or opening the site in a private
              window.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Button
                buttonType="button"
                label="Go back"
                size="medium"
                type="secondary"
                onClick={this.handleGoBack}
              >
                Go back
              </Button>
              <Button
                buttonType="button"
                label="Reload page"
                size="medium"
                type="primary"
                onClick={this.handleReload}
              >
                Reload page
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
